import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { getUserData } from "../auth";
import { useResetpasswordUserMutation } from "../apis/UsersApi";
import SuccessModal from "./SuccessModal"; // Import the SuccessModal component

const UserChangePassword = () => {
  const [isModalOpen, setIsModalOpen] = useState(false); // State to control modal visibility
  const [modalMessage, setModalMessage] = useState(""); // Store the modal message
  const [modalType, setModalType] = useState("success"); // Store the modal type (success, error, warning)
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  // Watch newPassword for comparison in confirmPassword
  const newPassword = watch("newPassword");

  // Mutation for resetting the password
  const [resetPasswordUser, { isLoading,  isError, error }] =
    useResetpasswordUserMutation();

  // Extract user data from localStorage
  const userData = getUserData();
  const userId = userData?.uid || "";

  // Submit handler
  const onSubmit = async (data) => {
    try {
      await resetPasswordUser({
        id: userId,
        password: data.newPassword,
      }).unwrap();
      
      // Set success message and open the modal
      setModalMessage("Password reset successful!");
      setModalType("success"); // Set modal type to success
      setIsModalOpen(true); // Open the modal on successful form submission
    } catch (err) {
      console.error("Password reset failed:", err);
      
      // Set error message and open the modal
      setModalMessage(error?.data?.message || "An error occurred during password reset.");
      setModalType("error"); // Set modal type to error
      setIsModalOpen(true); // Open the modal on error
    }
  };

  // Close the modal
  const closeModal = () => {
    setIsModalOpen(false); // Close the modal when called
  };

  // Navigate to profile (after modal close)
  const navigateToProfile = () => {
    // Implement the navigation logic here (e.g., using React Router)
    window.location.href = "/profile"; // For example, you can use window.location.href or useNavigate from react-router
  };

  return (
    <div className="contact-box border border-gray-100 rounded-16 px-24 py-40">
      {/* Pass modal visibility, message, type, and control functions as props to SuccessModal */}
      <SuccessModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        message={modalMessage}
        type={modalType} // Pass the dynamic type (success, error)
        navigateToProfile={navigateToProfile}
      />

      {/* Password Change Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <h6 className="mb-32">Reset Password</h6>
        <div className="row gy-4">
          <div className="col-sm-6">
            <label
              htmlFor="newPassword"
              className="text-sm font-heading-two text-gray-900 fw-semibold mb-4"
            >
              New Password{" "}
              <span className="text-danger text-xl line-height-1">*</span>
            </label>
            <input
              type="password"
              className="common-input px-16"
              id="newPassword"
              placeholder="Enter new password"
              {...register("newPassword", {
                required: "New password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              })}
            />
            {errors.newPassword && (
              <p className="text-danger text-sm">{errors.newPassword.message}</p>
            )}
          </div>

          <div className="col-sm-6">
            <label
              htmlFor="confirmPassword"
              className="text-sm font-heading-two text-gray-900 fw-semibold mb-4"
            >
              Confirm Password{" "}
              <span className="text-danger text-xl line-height-1">*</span>
            </label>
            <input
              type="password"
              className="common-input px-16"
              id="confirmPassword"
              placeholder="Confirm new password"
              {...register("confirmPassword", {
                required: "Confirm password is required",
                validate: (value) =>
                  value === newPassword || "Passwords do not match",
              })}
            />
            {errors.confirmPassword && (
              <p className="text-danger text-sm">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <div className="col-sm-12 mt-32">
            <button
              type="submit"
              className="btn btn-main py-18 px-32 rounded-8"
              disabled={isLoading}
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        </div>

        {isError && (
          <p className="text-danger text-sm mt-16">
            An error occurred: {error?.data?.message || "Please try again."}
          </p>
        )}
      </form>
    </div>
  );
};

export default UserChangePassword;
